import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import _ from 'lodash';

import Button from 'components/Button';
import {
  FieldsBox,
  FieldsFluid,
  Field,
  Select,
  Pills,
  Error,
} from 'components/Form';
import { CloseButton } from 'components/Common';

import IState from 'reducers';

import { getLearners } from '../home.action';
import {
  getInterestedServices,
  getGrades,
  getLocations,
  sendLearnerAdditionData,
  resetUserAdded,
  formDataFailure,
} from './learnersAdditionForm.action';

import {
  Title,
  Subtitle,
  AdditionalLearner,
  LearnersSection,
  LearnerBox,
  LearnerHeader,
  Text,
  SubmitSection,
  SubmitButtons,
  UserAddedTag,
  BoxNumber,
} from '../styles';
import {
  IFormData,
  IdbItem,
  IFormValues,
  initialState,
} from './LearnersAddition.interfaces';

export default function ContactUs() {
  const dispatch = useDispatch();

  const { locations, grades, interests, userAdded, formResponse } = useSelector(
    (state: IState) => state.learnersAddition
  );
  const [lastLearnerCreated, setLastLearnerCreated] = useState<number>(
    initialState.lastLearnerCreated
  );
  const [errors, setErrors] = useState<Array<string>>(initialState.errors);
  const [showError, setShowError] = useState<boolean>(initialState.showError);
  const [formValues, setFormValues] = useState<IFormValues>(
    initialState.formValues
  );
  const [showUserAdded, setShowUserAdded] = useState(false);
  const myRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const handleShowError = () => {
      if (formResponse.error) {
        if (formResponse.error === 'Request failed with status code 409') {
          setErrors((prevState) => [...prevState, 'alreadyExist']);
        } else {
          setShowError(true);
          setTimeout(() => setShowError(false), 5000);
        }
        setTimeout(() => dispatch(formDataFailure('')), 3000);
        myRef?.current?.scrollIntoView({ behavior: 'smooth' });
      }
    };
    dispatch(getLocations());
    dispatch(getGrades());
    dispatch(getInterestedServices());
    handleShowError();
  }, [dispatch, formResponse, showError]);

  const addAdditionalLearner = () => {
    setFormValues((prevState: IFormValues) => ({
      ...prevState,
      learnersGroup: {
        ...prevState.learnersGroup,
        [lastLearnerCreated + 1]: {
          firstName: '',
          grade: { value: 0, label: '' },
          interested: [],
          learnerIndex: lastLearnerCreated + 1,
          other: [],
          bundle: 0,
        },
      },
    }));
    setLastLearnerCreated((prevState) => prevState + 1);
  };

  useEffect(() => {
    if (userAdded) {
      setErrors(initialState.errors);
      setShowError(initialState.showError);
      setFormValues(initialState.formValues);
      setShowUserAdded(true);
      getLearners();
      setTimeout(() => {
        setShowUserAdded(false);
        dispatch(resetUserAdded());
      }, 4000);
    }
  }, [userAdded, dispatch]);

  const handleChange = (event) => {
    setErrors((prevState) => prevState.filter((e) => e !== event.target.name));
    if (event.target.name === 'email') {
      if (errors.includes('invalidEmail')) {
        setErrors((prevState) => prevState.filter((e) => e !== 'invalidEmail'));
      }
      if (errors.includes('alreadyExist')) {
        setErrors((prevState) => prevState.filter((e) => e !== 'alreadyExist'));
      }
    }

    if (event.target.name === 'phone') {
      if (errors.includes('invalidPhone')) {
        setErrors((prevState) => prevState.filter((e) => e !== 'invalidPhone'));
      }
      setFormValues((prevState) => ({
        ...prevState,
        phone: event.target.value.replace(/\D/g, ''),
      }));
    } else if (event.target.name.includes('learnersGroup')) {
      const learnerData = event.target.name.split('-');
      setFormValues((prevState: IFormValues) => ({
        ...prevState,
        learnersGroup: {
          ...prevState.learnersGroup,
          [learnerData[1]]: {
            ...prevState.learnersGroup[learnerData[1]],
            [learnerData[2]]: event.target.value,
          },
        },
      }));
    } else {
      setFormValues((prevState: IFormValues) => ({
        ...prevState,
        [event.target.name]:
          event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value,
      }));
    }
  };
  const handleChangeStateOfResidence = (event) => {
    setErrors((prevState) => prevState.filter((e) => e !== 'stateOfResidence'));
    setFormValues((prevState: IFormValues) => ({
      ...prevState,
      stateOfResidence: event,
    }));
  };

  const handleChangeGrade = (idx, event) => {
    setErrors((prevState) =>
      prevState.filter((e) => e !== `learnersGroup-${idx}-grade`)
    );
    setFormValues((prevState: IFormValues) => ({
      ...prevState,
      learnersGroup: {
        ...prevState.learnersGroup,
        [idx]: {
          ...prevState.learnersGroup[idx],
          grade: event,
        },
      },
    }));
  };

  const validateEmail = (email) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const re = /[0-9]{10,16}$/g;
    return re.test(`${phone}`);
  };

  const preSubmit = async () => {
    let count = 0;
    const learnersGroupArray = _.map(
      formValues.learnersGroup,
      (learner) => learner
    );

    if (!formValues.firstName && !errors.includes('firstName')) {
      setErrors((prevState) => [...prevState, 'firstName']);
      count += 1;
    }
    if (!formValues.lastName && !errors.includes('lastName')) {
      setErrors((prevState) => [...prevState, 'lastName']);
      count += 1;
    }
    if (
      !formValues.stateOfResidence.label &&
      !errors.includes('stateOfResidence')
    ) {
      setErrors((prevState) => [...prevState, 'stateOfResidence']);
      count += 1;
    }
    if (!formValues.email && !errors.includes('email')) {
      setErrors((prevState) => [...prevState, 'email']);
      count += 1;
    }
    if (formValues.email && !validateEmail(formValues.email)) {
      setErrors((prevState) => [...prevState, 'invalidEmail']);
      count += 1;
    }
    if (formValues.phone && !validatePhone(formValues.phone)) {
      setErrors((prevState) => [...prevState, 'invalidPhone']);
      count += 1;
    }
    learnersGroupArray.forEach((e) => {
      if (
        !formValues.learnersGroup[e.learnerIndex].firstName &&
        !errors.includes(`learnersGroup-${e.learnerIndex}-firstName`)
      ) {
        setErrors((prevState) => [
          ...prevState,
          `learnersGroup-${e.learnerIndex}-firstName`,
        ]);
        count += 1;
      }
      if (
        !formValues.learnersGroup[e.learnerIndex].grade.label &&
        !errors.includes(`learnersGroup-${e.learnerIndex}-grade`)
      ) {
        setErrors((prevState) => [
          ...prevState,
          `learnersGroup-${e.learnerIndex}-grade`,
        ]);
        count += 1;
      }
      if (
        !formValues.learnersGroup[e.learnerIndex].interested.length &&
        !formValues.learnersGroup[e.learnerIndex].other.length &&
        !errors.includes(`learnersGroup-${e.learnerIndex}-interested`)
      ) {
        setErrors((prevState) => [
          ...prevState,
          `learnersGroup-${e.learnerIndex}-interested`,
        ]);
        count += 1;
      }
    });
    return !!errors.length || !!count;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const {
      firstName,
      lastName,
      email,
      stateOfResidence,
      phone,
      learnersGroup,
    } = formValues;

    const anyError = await preSubmit();

    if (!anyError) {
      const formData: IFormData = {
        firstName,
        lastName,
        email,
        state: stateOfResidence.value,
        learners: _.map(learnersGroup, (learner) => ({
          alias: learner.firstName,
          grade: learner.grade.value,
          interestsTags: learner.interested,
          otherInterests: learner.other,
          parentScreenerStatus: 'COMPLETED',
          learnerScreenerStatus: 'COMPLETED',
          screenerReviewStatus: 'COMPLETED',
          initialPlanPayed: true,
          onboardingCompleted: true,
          sessionData: {
            duration: 15,
            quantity: learner.bundle > 0 ? 16 * learner.bundle : 0,
            type: 'DYNAMIC',
          },
        })),
        accessToMandatoryTech: true,
        termsAndConditions: true,
      };

      if (phone) {
        formData.phoneNumber = phone;
        formData.phoneNumber = `+1${phone}`;
      }

      dispatch(sendLearnerAdditionData(formData));
    }
  };

  const interestsOptions: Array<IdbItem> = interests
    .map((e) => ({
      value: e.id,
      label: e.name,
    }))
    .sort((a, b) => a.value - b.value);

  const gradesOptions: Array<IdbItem> = grades
    .map((e) => ({
      value: e.id,
      label: e.name,
    }))
    .sort((a, b) => a.value - b.value);
  const locationsOptions: Array<IdbItem> = locations
    .map((e) => ({
      value: e.id,
      label: e.name,
    }))
    .sort((a, b) => a.value - b.value);

  const removeAdditionalLearner = (learner) => {
    setFormValues((prevState: IFormValues) => ({
      ...prevState,
      learnersGroup: _.omit(prevState.learnersGroup, [learner.learnerIndex]),
    }));
  };

  const pickServicesPill = (id, val) => {
    setErrors((prevState) =>
      prevState.filter((e) => e !== `learnersGroup-${id}-interested`)
    );
    setFormValues((prevState: IFormValues) => ({
      ...prevState,
      learnersGroup: {
        ...prevState.learnersGroup,
        [id]: {
          ...prevState.learnersGroup[id],
          interested: prevState.learnersGroup[id].interested.includes(val)
            ? prevState.learnersGroup[id].interested.filter((e) => e !== val)
            : [...prevState.learnersGroup[id].interested, val],
        },
      },
    }));
  };

  const handleOther = (e, id) => {
    setErrors((prevState) =>
      prevState.filter((e) => e !== `learnersGroup-${id}-interested`)
    );
    setFormValues((prevState: IFormValues) => {
      const { value } = e.target;
      return {
        ...prevState,
        learnersGroup: {
          ...prevState.learnersGroup,
          [id]: {
            ...prevState.learnersGroup[id],
            other: value !== '' ? [value] : [],
          },
        },
      };
    });
  };

  const emailValidation = (errors: Array<string>) => {
    if (errors.includes('email')) {
      return 'Email is Required';
    }

    if (errors.includes('invalidEmail')) {
      return 'Invalid email';
    }

    if (errors.includes('alreadyExist')) {
      return 'Email in use!';
    }

    return false;
  };

  const optionSelectValidate = (value, options) =>
    options.some((v) => _.isEqual(value, v)) ? value : false;

  const learnersSection = () => {
    const learnersGroupArray = _.map(
      formValues.learnersGroup,
      (learner) => learner
    );

    return learnersGroupArray.map((learner) => {
      function handleClickPill(value) {
        pickServicesPill(learner.learnerIndex, value);
      }
      return (
        <LearnersSection key={learner.learnerIndex}>
          <LearnerBox>
            <LearnerHeader>
              {Object.keys(formValues.learnersGroup).length > 1 && (
                <CloseButton onClick={() => removeAdditionalLearner(learner)} />
              )}
            </LearnerHeader>
            <FieldsBox>
              <Field
                label="Name/Nickname*"
                name={`learnersGroup-${learner.learnerIndex}-firstName`}
                type="text"
                handleChange={handleChange}
                value={formValues.learnersGroup[learner.learnerIndex].firstName}
                error={
                  errors.includes(
                    `learnersGroup-${learner.learnerIndex}-firstName`
                  ) && 'First Name is Required'
                }
              />
              <Select
                label="Current Grade*"
                options={gradesOptions}
                name="gradesOptions"
                value={optionSelectValidate(
                  formValues.learnersGroup[learner.learnerIndex].grade,
                  gradesOptions
                )}
                placeholder="Select"
                handleChange={(e) => handleChangeGrade(learner.learnerIndex, e)}
                error={
                  errors.includes(
                    `learnersGroup-${learner.learnerIndex}-grade`
                  ) && 'Please select a grade'
                }
              />
            </FieldsBox>
            <FieldsFluid>
              <Text>
                <b>I’m interested in the following services:</b>
              </Text>
              {!!interestsOptions.length && (
                <>
                  <Pills
                    options={interestsOptions}
                    selections={learner?.interested}
                    handleClick={handleClickPill}
                    error={
                      errors.includes(
                        `learnersGroup-${learner.learnerIndex}-interested`
                      ) && `Please select o write a service`
                    }
                  />
                </>
              )}
            </FieldsFluid>
            <Field
              label="Other"
              name={`learnersGroup-${learner.learnerIndex}-other`}
              type="text"
              handleChange={(e) => {
                handleOther(e, learner.learnerIndex);
              }}
              value={
                formValues.learnersGroup[learner.learnerIndex].other[0] || ''
              }
              error={
                errors.includes(
                  `learnersGroup-${learner.learnerIndex}-interested`
                ) && 'Please select o write a service'
              }
            />
            <FieldsBox>
              <Field
                label="Bundle Bought"
                name={`learnersGroup-${learner.learnerIndex}-bundle`}
                type="number"
                handleChange={handleChange}
                min={0}
                value={formValues.learnersGroup[learner.learnerIndex].bundle}
              />
            </FieldsBox>
          </LearnerBox>
        </LearnersSection>
      );
    });
  };

  return (
    <div className="container-size">
      <Title>Create a new user or add a preexistent one</Title>
      <Subtitle ref={myRef}>Parent&apos;s info:</Subtitle>
      <form onSubmit={handleSubmit}>
        {showError && (
          <Error center>
            There is an error in one of the fields, please check it and try
            again.
          </Error>
        )}
        <FieldsBox>
          <Field
            label="First Name*"
            name="firstName"
            type="text"
            handleChange={handleChange}
            value={formValues.firstName}
            error={errors.includes('firstName') && 'First Name is Required'}
          />
          <Field
            label="Last Name*"
            name="lastName"
            type="text"
            handleChange={handleChange}
            value={formValues.lastName}
            error={errors.includes('lastName') && 'Last Name is Required'}
          />
        </FieldsBox>
        <FieldsBox>
          <Field
            label="Email Address*"
            name="email"
            type="text"
            handleChange={handleChange}
            value={formValues.email}
            error={emailValidation(errors)}
          />
          <BoxNumber>
            <Field
              label={`Mobile Number <span class="text-muted">(Optional)</span>`}
              name="phone"
              type="text"
              handleChange={handleChange}
              value={formValues.phone}
              error={errors.includes('invalidPhone') && 'Invalid phone number'}
            />
          </BoxNumber>
        </FieldsBox>
        <FieldsBox>
          <Select
            label="State of residence*"
            options={locationsOptions}
            name="stateOfResidence"
            value={optionSelectValidate(
              formValues.stateOfResidence,
              locationsOptions
            )}
            placeholder="Select"
            handleChange={handleChangeStateOfResidence}
            error={
              errors.includes('stateOfResidence') && 'Please select a state'
            }
            required
          />
        </FieldsBox>
        <Subtitle>Learner&apos;s info:</Subtitle>

        {learnersSection()}
        {Object.keys(formValues.learnersGroup).length < 4 && (
          <AdditionalLearner onClick={addAdditionalLearner}>
            <FontAwesomeIcon icon={faPlusCircle} size="lg" />
            <span>
              <b>Add additional learner</b>
            </span>
          </AdditionalLearner>
        )}
        {showUserAdded && <UserAddedTag>User added successfully!</UserAddedTag>}

        <SubmitSection>
          <SubmitButtons>
            <Button type="submit">Submit</Button>
          </SubmitButtons>
        </SubmitSection>
      </form>
    </div>
  );
}
